import { useEffect, useMemo, useRef, useState } from 'react'
import { View, Text, Linking } from 'react-native'
import { Redirect, useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Clock, Lock, Users } from '@/components/ui/icons'
import { AnimatedSpinner } from '@/components/ui/AnimatedSpinner'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { EmptyState } from '@/components/ui/EmptyState'
import {
  CheckoutEditorialRow,
  CheckoutPaymentRail,
  CheckoutSummaryColumn,
} from '@/components/booking/CheckoutEditorialLayout'
import { CheckoutPayFooter } from '@/components/payments/CheckoutPayFooter'
import { CheckoutMethodPicker } from '@/components/payments/CheckoutMethodPicker'
import { CheckoutSecureHeader } from '@/components/payments/CheckoutSecureHeader'
import {
  ToursCardForm,
  EMPTY_TOURS_CARD,
  toursCardIsValid,
  toursCardToChargeInput,
  type ToursCardValue,
} from '@/components/tours/ToursCardForm'
import { useSplitInfo, usePaySplit } from '@/lib/hooks/use-tours'
import { useAuth } from '@/lib/hooks/use-auth'
import { checkoutPayerFromProfile } from '@/lib/payments/charge-result'
import { guestChargeCents, type CheckoutPaymentMethod } from '@/lib/payments/processor-fee'
import { isPaypalOffered } from '@/lib/payments/paypal-offered'
import { toursChargeFailureI18nKey } from '@/lib/tours/payment-errors'
import { useTheme } from '@/lib/hooks/use-theme'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { useSemanticColors } from '@/lib/theme/tokens'
import { FONTS, SPACING } from '@/lib/brand'
import { formatCurrency, formatPrice } from '@/lib/utils/format'
import { formatTourDeparture } from '@/lib/tours/format'
import { notification, NotificationType } from '@/lib/utils/haptics'

export default function ToursSplitPayScreen() {
  const params = useLocalSearchParams<{ token?: string | string[] }>()
  const token = Array.isArray(params.token) ? params.token[0] : params.token
  const { t, i18n } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop, isTablet } = useBreakpoint()
  const isEditorial = isDesktop || isTablet
  const { data, isLoading, error } = useSplitInfo(token ?? '')
  const pay = usePaySplit()
  const { user, profile } = useAuth()
  const paypalOffered = isPaypalOffered()

  const [card, setCard] = useState<ToursCardValue>(EMPTY_TOURS_CARD)
  const [method, setMethod] = useState<CheckoutPaymentMethod>('card')
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false)

  useEffect(() => {
    setCard((current) =>
      current.email === '' && user?.email ? { ...current, email: user.email } : current,
    )
  }, [user?.email])

  const settled = Boolean(data && (data.status === 'settled' || done))
  const guestQuote = useMemo(
    () => guestChargeCents({ marketplaceCents: data?.amountCents ?? 0, method }),
    [data?.amountCents, method],
  )

  const settledHapticRef = useRef(false)
  useEffect(() => {
    if (settled && !settledHapticRef.current) {
      settledHapticRef.current = true
      void notification(NotificationType.Success)
    }
  }, [settled])

  if (!token) return <Redirect href="/" />

  if (isLoading) {
    return (
      <ScreenContainer variant="full-bleed" scroll={false}>
        <View style={{ flex: 1, justifyContent: 'center' }} testID="tours.split-loading">
          <AnimatedSpinner size={36} color={colors.textAccent} message={t('tours.loading')} />
        </View>
      </ScreenContainer>
    )
  }

  if (error || !data) {
    return (
      <ScreenContainer variant="full-bleed" scroll={false}>
        <View style={{ flex: 1, justifyContent: 'center' }}>
          <EmptyState
            illustration={<Lock size={44} color={colors.textMuted} strokeWidth={1.5} />}
            title={t('tours.errors.split_not_found')}
            description={t('tours.bookings.error_body')}
          />
        </View>
      </ScreenContainer>
    )
  }

  if (settled) {
    return (
      <ScreenContainer variant="full-bleed" scroll={false}>
        <View
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            paddingHorizontal: SPACING.lg,
          }}
        >
          <Text
            style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textMuted }}
            testID="tours.split-secure-brand"
          >
            {t('tours.split.secure_brand')}
          </Text>
          <View style={{ alignItems: 'center', paddingTop: SPACING['3xl'] }}>
            <CheckCircle2 size={56} color={colors.success} strokeWidth={1.6} />
            <Text
              style={{
                marginTop: SPACING.lg,
                fontFamily: FONTS.bodyBold,
                fontSize: 22,
                color: colors.textPrimary,
                textAlign: 'center',
              }}
              testID="tours.split-paid"
            >
              {t('tours.split.paid_title')}
            </Text>
            <Text
              style={{
                marginTop: SPACING.sm,
                maxWidth: 360,
                fontFamily: FONTS.body,
                fontSize: 15,
                lineHeight: 22,
                color: colors.textSecondary,
                textAlign: 'center',
              }}
            >
              {t('tours.split.paid_body')}
            </Text>
          </View>
        </View>
      </ScreenContainer>
    )
  }

  const paypalPayer = checkoutPayerFromProfile({
    email: card.email || user?.email,
    firstName: profile?.firstName,
    lastName: profile?.lastName,
    phone: profile?.phone,
  })
  const paypalReady = paypalPayer.email.includes('@')

  const submit = async () => {
    setErrorKey(null)
    if (method === 'card' && !toursCardIsValid(card)) {
      setErrorKey('tours.checkout.invalid_card')
      return
    }
    if (method === 'paypal' && !paypalReady) {
      setErrorKey('tours.checkout.email_required')
      return
    }
    const { card: cardInput, payer } = toursCardToChargeInput(card)
    try {
      const result = await pay.mutateAsync({
        token: data.shareToken,
        payer:
          method === 'paypal'
            ? {
                ...paypalPayer,
                firstName: paypalPayer.firstName || 'Tago',
                lastName: paypalPayer.lastName || 'Guest',
              }
            : payer,
        card: method === 'card' ? cardInput : undefined,
        provider: method === 'paypal' ? 'paypal' : undefined,
      })
      if (result.status === 'captured') {
        setDone(true)
        return
      }
      if (result.challenge) {
        const opened = await Linking.openURL(result.challenge.url)
          .then(() => true)
          .catch(() => false)
        if (!opened) {
          setErrorKey('tours.errors.payment_failed')
          return
        }
      }
      setAwaitingConfirmation(true)
    } catch (err) {
      setErrorKey(toursChargeFailureI18nKey(err))
    }
  }

  const sharePosition = data.position && data.totalParticipants ? data.position : null
  const paypalDelta = Math.max(0, guestQuote.chargeCents - data.amountCents)

  return (
    <ScreenContainer variant="full-bleed" scroll={false}>
      <KeyboardAwareView
        includeTopInset
        style={{ flex: 1, width: '100%' }}
        contentContainerStyle={{
          paddingHorizontal: isEditorial ? 0 : SPACING.lg,
          paddingBottom: SPACING.lg,
        }}
      >
        <CheckoutEditorialRow
          editorial={isEditorial}
          padX={SPACING.lg}
          testID="tours.split-editorial-layout"
        >
          <CheckoutSummaryColumn editorial={isEditorial}>
            <View style={{ paddingTop: SPACING.lg, gap: SPACING.md }} testID="tours.split-receipt">
              <Text
                style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textMuted }}
                testID="tours.split-secure-brand"
              >
                {t('tours.split.secure_brand')}
              </Text>
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 26,
                  lineHeight: 32,
                  color: colors.textPrimary,
                }}
              >
                {t('tours.split.title')}
              </Text>
              <Text
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 15,
                  color: colors.textSecondary,
                }}
              >
                {t('tours.split.subtitle')}
              </Text>
              {data.title ? (
                <Text
                  style={{ fontFamily: FONTS.bodyBold, fontSize: 18, color: colors.textPrimary }}
                >
                  {data.title}
                </Text>
              ) : null}
              {data.originLabel && data.destinationLabel ? (
                <Text style={{ fontFamily: FONTS.body, fontSize: 14, color: colors.textSecondary }}>
                  {t('tours.route', {
                    origin: data.originLabel,
                    destination: data.destinationLabel,
                  })}
                </Text>
              ) : null}
              {data.startsAt ? (
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Clock size={14} color={colors.textMuted} strokeWidth={1.8} />
                  <Text
                    style={{
                      marginLeft: 4,
                      fontFamily: FONTS.body,
                      fontSize: 14,
                      color: colors.textSecondary,
                    }}
                  >
                    {formatTourDeparture(data.startsAt, i18n.language)}
                  </Text>
                </View>
              ) : null}
              {data.passengerCount ? (
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Users size={14} color={colors.textMuted} strokeWidth={1.8} />
                  <Text
                    style={{
                      marginLeft: 4,
                      fontFamily: FONTS.body,
                      fontSize: 14,
                      color: colors.textMuted,
                    }}
                  >
                    {data.passengerCount} · {t('tours.seats')}
                  </Text>
                </View>
              ) : null}
              {sharePosition ? (
                <Text
                  style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textMuted }}
                  testID="tours.split-position"
                >
                  {t('tours.split.part_context', {
                    position: sharePosition,
                    total: data.totalParticipants ?? 0,
                  })}
                </Text>
              ) : null}
              <View
                style={{
                  paddingTop: SPACING.md,
                  borderTopWidth: 1,
                  borderTopColor: colors.border,
                }}
              >
                <Text style={{ fontFamily: FONTS.body, fontSize: 15, color: colors.textSecondary }}>
                  {t('tours.split.your_share')}
                </Text>
                <Text
                  style={{
                    marginTop: 4,
                    fontFamily: FONTS.bodyBold,
                    fontSize: 24,
                    lineHeight: 28,
                    color: colors.textAccent,
                  }}
                  testID="tours.split-amount"
                >
                  {formatCurrency(guestQuote.chargeCents)}
                </Text>
                {paypalDelta > 0 ? (
                  <Text
                    style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textMuted }}
                    testID="tours.split-paypal-fee"
                  >
                    {t('checkout.paypal_fee_line')} · {formatPrice(paypalDelta)}
                  </Text>
                ) : null}
                {data.totalCents && data.totalCents > data.amountCents ? (
                  <Text
                    style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textMuted }}
                    testID="tours.split-of-total"
                  >
                    {t('tours.split.of_total', { amount: formatCurrency(data.totalCents) })}
                  </Text>
                ) : null}
              </View>
            </View>
          </CheckoutSummaryColumn>

          <CheckoutPaymentRail
            editorial={isEditorial}
            wide={isDesktop}
            testID="tours.split-payment-rail"
            footer={
              <CheckoutPayFooter
                title={
                  awaitingConfirmation
                    ? t('tours.split.pending_title')
                    : method === 'paypal'
                      ? t('checkout.pay_with_paypal', {
                          amount: formatCurrency(guestQuote.chargeCents),
                        })
                      : t('tours.split.pay', { amount: formatCurrency(guestQuote.chargeCents) })
                }
                onPress={() => void submit()}
                loading={pay.isPending}
                disabled={awaitingConfirmation}
                testID="tours.split-pay"
                trustText={t('tours.checkout.secure_note')}
                errorText={errorKey ? t(errorKey) : null}
                errorTestID="tours.split-error"
                hintText={
                  awaitingConfirmation
                    ? t('tours.split.pending_body')
                    : method === 'paypal'
                      ? t('checkout.processor_fee_hint')
                      : null
                }
              />
            }
          >
            <View style={{ gap: SPACING.md }}>
              <CheckoutSecureHeader
                title={
                  method === 'paypal'
                    ? t('checkout.paypal_secure_title')
                    : t('checkout.secure_payment_title')
                }
                description={
                  method === 'paypal'
                    ? t('checkout.paypal_secure_desc')
                    : t('checkout.secure_payment_desc')
                }
              />
              {paypalOffered ? (
                <CheckoutMethodPicker
                  method={method}
                  onChange={setMethod}
                  title={t('checkout.payment_method')}
                  cardLabel={t('checkout.card')}
                  paypalLabel={t('checkout.paypal')}
                />
              ) : null}
              {method === 'card' ? <ToursCardForm value={card} onChange={setCard} /> : null}
            </View>
          </CheckoutPaymentRail>
        </CheckoutEditorialRow>
      </KeyboardAwareView>
    </ScreenContainer>
  )
}
